// frontend/src/utils/itemPlanningParent.js
//
// Where an item-location's inventory target is inherited FROM, and what it
// resolves to. The item-location is the leaf of the Store → Supplier →
// Item-location chain (api.services.planning_defaults): a row with a null
// `inventory_target_mode` / `target_days_of_supply` reads its item's DEFAULT
// SUPPLIER — or the store, when the item has no supplier yet. The Item setup
// grid names the parent per row, for the inherit option ("Supplier's
// setting: Days of supply"), the hover, and the placeholder an inheriting box
// shows.
//
// The nested `default_supplier` object is the SupplierSerializer rendering,
// which carries the supplier's own `effective_*` pair.

/**
 * @param {object|null} defaultSupplier  the item's nested `default_supplier`
 *   wire object, or null when the item has no supplier.
 * @param {object|null} storePlanning  the store's planning defaults
 *   (hooks/useStorePlanning); null while unknown.
 * @returns {{level: 'supplier'|'store', name: string|null,
 *   inventory_target_mode: string, target_days_of_supply: number} | null}
 *   null when the row has no supplier and the store's defaults are not known —
 *   nothing can be named, so the caller keeps its controls read-only.
 */
export function planningParentOf(defaultSupplier, storePlanning) {
  if (defaultSupplier) {
    return {
      level: 'supplier',
      name: defaultSupplier.supplier_name,
      inventory_target_mode: defaultSupplier.effective_inventory_target_mode,
      target_days_of_supply: defaultSupplier.effective_target_days_of_supply,
    };
  }
  if (storePlanning === null) return null;
  return {
    level: 'store',
    name: null,
    inventory_target_mode: storePlanning.inventory_target_mode,
    target_days_of_supply: storePlanning.target_days_of_supply,
  };
}

/**
 * Re-derive a row's two `effective_*` readings from its overrides and its
 * parent — what the serializer will answer once the row is saved, applied
 * now so a dependent control (the Days-of-supply cell's muting) follows a
 * mode edit or a supplier swap in the same render.
 *
 * A row whose parent cannot be named keeps the readings the server sent.
 * Mutates and returns `row` (an AG Grid row object is edited in place).
 */
export function resolveItemEffective(row, parent) {
  if (parent === null) return row;
  row.effective_inventory_target_mode = row.inventory_target_mode === null
    ? parent.inventory_target_mode : row.inventory_target_mode;
  row.effective_target_days_of_supply = row.target_days_of_supply === null
    ? parent.target_days_of_supply : row.target_days_of_supply;
  return row;
}
